import { Box, makeStyles, Theme } from "@material-ui/core";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import Typography from "@material-ui/core/Typography";
import DeleteForeverOutlinedIcon from "@material-ui/icons/DeleteForeverOutlined";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import { useSnackbar } from "notistack";
import React, { useState } from "react";
import { useIntl } from "react-intl";
import { store } from "../../../..";
import { initModulePayment } from "../../../../api/CartApi";
import { SwissGermanLocale, SwissTimeZone } from "../../../../data/constants";
import { TransactionStatus } from "../../../../model/Cart/TransactionStatus";
import { ModuleSelectionViewModel } from "../../../../model/ExamSignup/ModuleSelectionViewModel";
import { GetTranslatedValueIntl } from "../../../../model/Shared/TranslatedValue";
import CustomButton from "../../../common/buttons/CustomButton";
import CustomDraggableDialog from "../../../common/customDialog/CustomDraggableDialog";
import { LoadingSpinner } from "../../../common/LoadingSpinner";
import { PaymentMethodType } from "../../../payment/PaymentConstants";
import PaymentMethodSelectorDialog from "../../../payment/PaymentMethodSelectorDialog";
import TranslatedMessage from "../../../translations/TranslatedMessage";
import { useSignUpContext } from "../../CertificationDataWizardContext/CertificationDataWizardContext";
import SummaryExamRow from "./SummaryExamRow";
import { TranslationKey } from "../../../../translations/TranslationKey";
import { GetTranslation } from "../../../../translations/GetTranslation";
import Alert from "@material-ui/lab/Alert/Alert";
import { ExamType } from "../../../../model/Exam";

type SummaryModuleRowProps = {
  module: ModuleSelectionViewModel;
  onRemoveModule: (module: ModuleSelectionViewModel) => void;
  onPayModule: (module: ModuleSelectionViewModel) => void;
  onPaymentError: () => void;
  isLoading: boolean;
};

const useStyles = makeStyles((theme: Theme) => ({
  freeCancellationDate: {
    fontStyle: "italic",
    marginLeft: theme.spacing(3),
  },
  pointer: {
    cursor: "pointer",
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1) + "px",
  },
  payButton: {
    height: "auto !important",
  },
  fullyBooked: {
    display: "inline-flex",
    padding: `0px ${theme.spacing(2)}px`,
  },
  moduleInfo: {
    display: "flex",
    gap: theme.spacing(1) + "px",
  },
}));

const SummaryModuleRow: React.FunctionComponent<SummaryModuleRowProps> = ({
  module,
  onRemoveModule,
  onPayModule,
  onPaymentError,
  isLoading,
}) => {
  const { referenceModules, availableExamSessions } = useSignUpContext();
  const [confirmRemoveDialog, setConfirmRemoveDialog] = useState(false);
  const [showPaymentSelector, setShowPaymentSelector] = useState(false);
  const deviceFingerprint = store.getState().sessionState.sessionId;
  const [busy, setBusy] = useState(false);

  const intl = useIntl();
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const moduleHasErrors = (module.errors?.length ?? 0) > 0;

  const referenceModule = referenceModules.find(
    (m) => m.examSessionId === module.examSessionId && m.courseId === module.courseId && m.id === module.moduleId
  );
  if (!referenceModule) {
    console.warn("Reference Module not found for ModuleSelection: ", module);
    return null;
  }

  const examSession = availableExamSessions.find((e) => e.id === module.examSessionId);
  if (!examSession) {
    console.warn("Exam Session not found for ModuleSelection: ", module);
    return null;
  }

  const buttonLabel = module.paid ? (
    referenceModule.price > 0 ? (
      <TranslatedMessage id="startCertification.paymentAndCompletionStep.button.paid" />
    ) : (
      <TranslatedMessage id="startCertification.paymentAndCompletionStep.button.confirmed" />
    )
  ) : referenceModule.price > 0 ? (
    <TranslatedMessage id="startCertification.paymentAndCompletionStep.button.pay" />
  ) : (
    <TranslatedMessage id="startCertification.paymentAndCompletionStep.button.confirm" />
  );

  const onPayButtonClick = (module: ModuleSelectionViewModel) => {
    if (module.price > 0) {
      setShowPaymentSelector(true);
    } else {
      startModulePayment(PaymentMethodType.CreditCard);
    }
  };

  const startModulePayment = (paymentMethod: PaymentMethodType) => {
    setBusy(true);
    initModulePayment({
      moduleId: module.moduleId,
      examSessionId: module.examSessionId,
      courseId: module.courseId,
      examDetails: module.examDetails!,
      paymentMethod,
      deviceFingerprint,
    })
      .then((response) => {
        if (response.success) {
          if (response.data.paymentUrl) {
            window.open(response.data.paymentUrl, "_self");
          } else if (response.data.status === TransactionStatus.Paid) {
            enqueueSnackbar(
              <TranslatedMessage
                id="startCertification.paymentAndCompletionStep.messages.transactionConfirmed"
                values={{ id: response.data.transactionId }}
              />,
              { variant: "success" }
            );

            onPayModule(module);
          }
        } else {
          onPaymentError();
        }
      })
      .catch(() => {
        onPaymentError();
      })
      .finally(() => setBusy(false));
  };

  const handlePay = (paymentMethod: PaymentMethodType) => {
    setShowPaymentSelector(false);
    startModulePayment(paymentMethod);
  };

  const handleRemoveModule = () => {
    setConfirmRemoveDialog(false);
    onRemoveModule(module);
  };

  const handleCancelRemove = () => {
    setConfirmRemoveDialog(false);
  };

  const moduleFullyBooked =
    module?.examDetails &&
    module.paid !== true &&
    (module?.examDetails?.filter((d) => d.isFullyBooked && d.examType !== ExamType.TermPaper) ?? ["no_exam_details"])
      .length > 0;

  return (
    <React.Fragment>
      <TableRow>
        <TableCell colSpan={5}>
          <Box className={classes.moduleInfo}>
            <b>{GetTranslatedValueIntl(intl, referenceModule.name)}</b>
            <Typography variant="subtitle2" component="span" className={classes.freeCancellationDate}>
              {"("}
              <TranslatedMessage id="startCertification.paymentAndCompletionStep.freeCancelDate" />
              {new Date(examSession.freeCancellationEndDate).toLocaleDateString(SwissGermanLocale, {
                timeZone: SwissTimeZone,
              })}
              {")"}
            </Typography>
            {!isLoading && moduleFullyBooked && (
              <Alert severity="error" className={classes.fullyBooked}>
                <TranslatedMessage id="startCertification.paymentAndCompletionStep.moduleFullyBooked" />
              </Alert>
            )}
          </Box>
        </TableCell>
        <TableCell align="right">
          {referenceModule.price.toLocaleString(SwissGermanLocale, { minimumFractionDigits: 2 })}
        </TableCell>
        <TableCell align="center" className={classes.actions}>
          {isLoading && (
            <CustomButton disabled={true} color="primary" tooltip={""} onClick={() => {}}>
              &nbsp;
            </CustomButton>
          )}
          {!isLoading && (
            <>
              <CustomButton
                color="primary"
                tooltip={buttonLabel}
                onClick={() => onPayButtonClick(module)}
                disabled={moduleHasErrors || module.paid || moduleFullyBooked}
                internalButtonClassName={classes.payButton}
              >
                {buttonLabel}
              </CustomButton>
              {!module.paid && (
                <DeleteForeverOutlinedIcon
                  color="primary"
                  className={classes.pointer}
                  onClick={() => setConfirmRemoveDialog(true)}
                ></DeleteForeverOutlinedIcon>
              )}
            </>
          )}
          {moduleHasErrors && (
            <ErrorOutlineIcon
              color="error"
              titleAccess={module.errors?.map((err) => GetTranslation(intl, err as TranslationKey)).join("\n")}
            />
          )}
          {confirmRemoveDialog && (
            <CustomDraggableDialog
              title={<TranslatedMessage id="startCertification.paymentAndCompletionStep.removeModule" />}
              isOpen={true}
              content={<TranslatedMessage id="startCertification.paymentAndCompletionStep.removeModuleConfirmation" />}
              acceptText={<TranslatedMessage id="common.yes" />}
              cancelText={<TranslatedMessage id="common.no" />}
              onAccept={handleRemoveModule}
              onCancel={handleCancelRemove}
            ></CustomDraggableDialog>
          )}
        </TableCell>
      </TableRow>
      {referenceModule.exams.map((exam, index) => (
        <SummaryExamRow key={index} module={module} examReference={exam} />
      ))}
      {showPaymentSelector && (
        <PaymentMethodSelectorDialog
          onClose={() => setShowPaymentSelector(false)}
          onPay={handlePay}
        ></PaymentMethodSelectorDialog>
      )}
      {busy && (
        <TableRow>
          <TableCell colSpan={5}>
            <LoadingSpinner />
          </TableCell>
        </TableRow>
      )}
    </React.Fragment>
  );
};

export default SummaryModuleRow;
